import React, { useCallback, useEffect, useMemo, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/CustomComponents/Switchery/ToggleSwitch.css";
import "./TwilioPlatformConfig.css";

const SID_HINTS = {
  parentAccountSid: "Usually starts with AC",
  apiKeySid: "Usually starts with SK",
};

function formatUpdatedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SecretField({
  id,
  label,
  name,
  value,
  placeholder,
  required,
  error,
  hint,
  masked,
  showValue,
  onToggleShow,
  onChange,
  onCopyMasked,
}) {
  return (
    <div className="twilio-platform__field">
      <div className="twilio-platform__label-row">
        <label className="twilio-platform__label" htmlFor={id}>
          {label}
          {required ? <span className="twilio-platform__required">*</span> : null}
        </label>
      </div>
      <div className="twilio-platform__input-wrap">
        <input
          id={id}
          name={name}
          type={showValue ? "text" : "password"}
          className={`twilio-platform__input twilio-platform__input--with-actions${
            error ? " is-invalid" : ""
          }`}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          autoComplete="new-password"
          spellCheck={false}
        />
        <div className="twilio-platform__input-actions">
          <button
            type="button"
            className="twilio-platform__icon-btn"
            onClick={onToggleShow}
            title={showValue ? "Hide value" : "Show value"}
            aria-label={showValue ? "Hide value" : "Show value"}
          >
            <i className={`fas ${showValue ? "fa-eye-slash" : "fa-eye"}`} aria-hidden />
          </button>
        </div>
      </div>
      {error ? (
        <p className="twilio-platform__error">
          <i className="fas fa-circle-exclamation" aria-hidden />
          {error}
        </p>
      ) : null}
      {masked ? (
        <div className="twilio-platform__masked">
          <i className="fas fa-shield-halved" aria-hidden />
          <span>Saved: {masked}</span>
          {onCopyMasked ? (
            <button
              type="button"
              className="twilio-platform__icon-btn"
              onClick={onCopyMasked}
              title="Copy masked value"
              aria-label="Copy masked value"
              style={{ width: 26, height: 26 }}
            >
              <i className="fas fa-copy" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}
      {hint && !error ? <p className="twilio-platform__hint">{hint}</p> : null}
    </div>
  );
}

function TextField({
  id,
  label,
  name,
  value,
  placeholder,
  required,
  error,
  hint,
  onChange,
  onCopy,
}) {
  return (
    <div className="twilio-platform__field">
      <div className="twilio-platform__label-row">
        <label className="twilio-platform__label" htmlFor={id}>
          {label}
          {required ? <span className="twilio-platform__required">*</span> : null}
        </label>
      </div>
      <div className="twilio-platform__input-wrap">
        <input
          id={id}
          name={name}
          type="text"
          className={`twilio-platform__input twilio-platform__input--with-actions${
            error ? " is-invalid" : ""
          }`}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          autoComplete="off"
          spellCheck={false}
        />
        <div className="twilio-platform__input-actions">
          <button
            type="button"
            className="twilio-platform__icon-btn"
            onClick={onCopy}
            disabled={!value}
            title="Copy"
            aria-label={`Copy ${label}`}
          >
            <i className="fas fa-copy" aria-hidden />
          </button>
        </div>
      </div>
      {error ? (
        <p className="twilio-platform__error">
          <i className="fas fa-circle-exclamation" aria-hidden />
          {error}
        </p>
      ) : null}
      {hint && !error ? <p className="twilio-platform__hint">{hint}</p> : null}
    </div>
  );
}

const ManageTwilioPlatformConfig = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showApiKeySecret, setShowApiKeySecret] = useState(false);
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    parentAccountSid: "",
    apiKeySid: "",
    apiKeySecret: "",
    authToken: "",
    status: true,
    hasApiKeySecret: false,
    apiKeySecretMasked: "",
    hasAuthToken: false,
    authTokenMasked: "",
    isConfigured: false,
    updatedDateTime: "",
  });

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_TWILIO_PLATFORM_CONFIG);
      const data = response.data?.data || {};
      setFormData({
        parentAccountSid: data.parentAccountSid ?? data.ParentAccountSid ?? "",
        apiKeySid: data.apiKeySid ?? data.ApiKeySid ?? "",
        apiKeySecret: "",
        authToken: "",
        status: data.status ?? data.Status ?? true,
        hasApiKeySecret: data.hasApiKeySecret ?? data.HasApiKeySecret ?? false,
        apiKeySecretMasked: data.apiKeySecretMasked ?? data.ApiKeySecretMasked ?? "",
        hasAuthToken: data.hasAuthToken ?? data.HasAuthToken ?? false,
        authTokenMasked: data.authTokenMasked ?? data.AuthTokenMasked ?? "",
        isConfigured: data.isConfigured ?? data.IsConfigured ?? false,
        updatedDateTime: data.updatedDateTime ?? data.UpdatedDateTime ?? "",
      });
      setErrors({});
      setShowApiKeySecret(false);
      setShowAuthToken(false);
    } catch {
      showErrorToast("Failed to load Twilio platform configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleToggleStatus = () => {
    setFormData((prev) => ({ ...prev, status: !prev.status }));
  };

  const copyText = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showSuccessToast(`${label} copied`);
    } catch {
      showErrorToast("Could not copy to clipboard");
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.apiKeySid?.trim()) {
      next.apiKeySid = "API Key SID is required";
    } else if (
      formData.apiKeySid.trim().length < 10 ||
      !/^SK/i.test(formData.apiKeySid.trim())
    ) {
      next.apiKeySid = "API Key SID should start with SK";
    }

    if (
      formData.parentAccountSid?.trim() &&
      !/^AC/i.test(formData.parentAccountSid.trim())
    ) {
      next.parentAccountSid = "Account SID should start with AC";
    }

    if (!formData.hasApiKeySecret && !formData.apiKeySecret?.trim()) {
      next.apiKeySecret = "API Key Secret is required for the first save";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      showErrorToast("Please fix the highlighted fields");
      return;
    }

    try {
      setSaving(true);
      const response = await axiosInstance.put(APIs.SAVE_TWILIO_PLATFORM_CONFIG, {
        parentAccountSid: formData.parentAccountSid?.trim() || null,
        apiKeySid: formData.apiKeySid.trim(),
        apiKeySecret: formData.apiKeySecret?.trim() ?? "",
        authToken: formData.authToken?.trim() || null,
        status: formData.status,
      });

      if (response.data?.status) {
        showSuccessToast("Twilio platform configuration saved");
        fetchConfig();
      } else {
        showErrorToast(response.data?.message || "Could not save configuration");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const checklist = useMemo(
    () => [
      {
        key: "apiKeySid",
        label: "API Key SID set",
        ok: Boolean(formData.apiKeySid?.trim()),
      },
      {
        key: "apiKeySecret",
        label: formData.hasApiKeySecret
          ? "API Key Secret saved"
          : "API Key Secret ready to save",
        ok: formData.hasApiKeySecret || Boolean(formData.apiKeySecret?.trim()),
      },
      {
        key: "authToken",
        label: formData.hasAuthToken || formData.authToken?.trim()
          ? "Auth Token ready for webhooks"
          : "Auth Token for webhooks",
        ok: formData.hasAuthToken || Boolean(formData.authToken?.trim()),
        optional: true,
      },
      {
        key: "status",
        label: formData.status ? "Platform status Active" : "Platform status Inactive",
        ok: formData.status,
      },
    ],
    [formData]
  );

  const statusTone = !formData.isConfigured
    ? "warn"
    : formData.status
      ? "ok"
      : "off";

  const statusTitle =
    statusTone === "ok"
      ? "Platform keys are configured"
      : statusTone === "off"
        ? "Keys saved but platform is inactive"
        : "Platform keys are not fully configured";

  const statusMeta =
    statusTone === "warn"
      ? "Add an API Key SID and Secret so restaurants can use ServeIQ Twilio."
      : formData.updatedDateTime
        ? `Last updated ${formatUpdatedAt(formData.updatedDateTime)}`
        : "Credentials are stored securely. Secrets are never shown in full.";

  return (
    <div
      className="twilio-platform"
      style={{
        "--tp-accent": themeColor,
        "--tp-accent-soft": `${themeColor}22`,
      }}
    >
      <div className="twilio-platform__header">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/">Home</a>
            </li>
            <li className="breadcrumb-item active">Twilio Platform Settings</li>
          </ol>
          <h1 className="page-header mb-0">Twilio Platform Settings</h1>
          <p
            className="mb-0 mt-1 small"
            style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}
          >
            Parent account API keys used by restaurants that enable &quot;Use ServeIQ
            Twilio&quot;.
          </p>
        </div>
        <div className="twilio-platform__header-actions">
          <button
            type="button"
            className="twilio-platform__btn twilio-platform__btn--ghost"
            onClick={fetchConfig}
            disabled={loading || saving}
          >
            Discard changes
          </button>
          <button
            type="button"
            className="twilio-platform__btn twilio-platform__btn--primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            <i className={`fas ${saving ? "fa-spinner fa-spin" : "fa-save"}`} aria-hidden />
            {saving ? "Saving…" : "Save Configuration"}
          </button>
        </div>
      </div>

      <GridSectionLoader loading={loading} minHeight="360px">
        {!loading && (
          <>
            <div className={`twilio-platform__status twilio-platform__status--${statusTone}`}>
              <div className="twilio-platform__status-main">
                <span className="twilio-platform__status-icon">
                  <i
                    className={`fas ${
                      statusTone === "ok"
                        ? "fa-circle-check"
                        : statusTone === "off"
                          ? "fa-pause-circle"
                          : "fa-triangle-exclamation"
                    }`}
                    aria-hidden
                  />
                </span>
                <div>
                  <p className="twilio-platform__status-title">{statusTitle}</p>
                  <p className="twilio-platform__status-meta">{statusMeta}</p>
                </div>
              </div>
              <span className={`twilio-platform__badge twilio-platform__badge--${statusTone}`}>
                {statusTone === "ok"
                  ? "Configured"
                  : statusTone === "off"
                    ? "Inactive"
                    : "Incomplete"}
              </span>
            </div>

            <div className="twilio-platform__grid">
              <div className="twilio-platform__card">
                <div className="twilio-platform__card-head">
                  <span className="twilio-platform__card-head-icon">
                    <i className="fas fa-key" aria-hidden />
                  </span>
                  <div>
                    <h2 className="twilio-platform__card-title">API credentials</h2>
                    <p className="twilio-platform__card-sub">
                      Used when a restaurant selects ServeIQ Twilio. Leave secret fields
                      blank to keep the currently saved values.
                    </p>
                  </div>
                </div>
                <div className="twilio-platform__card-body">
                  <TextField
                    id="parentAccountSid"
                    label="Parent Account SID"
                    name="parentAccountSid"
                    value={formData.parentAccountSid}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    error={errors.parentAccountSid}
                    hint={SID_HINTS.parentAccountSid}
                    onChange={handleInputChange}
                    onCopy={() =>
                      copyText(formData.parentAccountSid, "Parent Account SID")
                    }
                  />
                  <TextField
                    id="apiKeySid"
                    label="API Key SID"
                    name="apiKeySid"
                    value={formData.apiKeySid}
                    placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    required
                    error={errors.apiKeySid}
                    hint={SID_HINTS.apiKeySid}
                    onChange={handleInputChange}
                    onCopy={() => copyText(formData.apiKeySid, "API Key SID")}
                  />
                  <SecretField
                    id="apiKeySecret"
                    label="API Key Secret"
                    name="apiKeySecret"
                    value={formData.apiKeySecret}
                    placeholder={
                      formData.hasApiKeySecret
                        ? "Leave blank to keep existing secret"
                        : "Enter API Key Secret"
                    }
                    required={!formData.hasApiKeySecret}
                    error={errors.apiKeySecret}
                    hint="Only enter a new value when rotating the key."
                    masked={formData.apiKeySecretMasked}
                    showValue={showApiKeySecret}
                    onToggleShow={() => setShowApiKeySecret((v) => !v)}
                    onChange={handleInputChange}
                  />
                  <SecretField
                    id="authToken"
                    label="Auth Token (inbound webhooks)"
                    name="authToken"
                    value={formData.authToken}
                    placeholder={
                      formData.hasAuthToken
                        ? "Leave blank to keep existing auth token"
                        : "Enter parent Auth Token"
                    }
                    error={errors.authToken}
                    hint="Needed to validate inbound Twilio webhook requests."
                    masked={formData.authTokenMasked}
                    showValue={showAuthToken}
                    onToggleShow={() => setShowAuthToken((v) => !v)}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="twilio-platform__status-panel">
                <div className="twilio-platform__card">
                  <div className="twilio-platform__card-head">
                    <span className="twilio-platform__card-head-icon">
                      <i className="fas fa-toggle-on" aria-hidden />
                    </span>
                    <div>
                      <h2 className="twilio-platform__card-title">Platform status</h2>
                      <p className="twilio-platform__card-sub">
                        Inactive keys are ignored by restaurants, even if saved.
                      </p>
                    </div>
                  </div>
                  <div className="twilio-platform__card-body">
                    <div className="twilio-platform__toggle-row">
                      <div className="twilio-platform__toggle-copy">
                        <p className="twilio-platform__toggle-title">
                          {formData.status ? "Active" : "Inactive"}
                        </p>
                        <p className="twilio-platform__toggle-sub">
                          {formData.status
                            ? "Restaurants can use ServeIQ Twilio credentials."
                            : "ServeIQ Twilio is disabled until you reactivate."}
                        </p>
                      </div>
                      <ToggleSwitch
                        isChecked={formData.status}
                        onToggle={handleToggleStatus}
                        label=""
                        disabled={false}
                      />
                    </div>
                  </div>
                </div>

                <div className="twilio-platform__card">
                  <div className="twilio-platform__card-head">
                    <span className="twilio-platform__card-head-icon">
                      <i className="fas fa-list-check" aria-hidden />
                    </span>
                    <div>
                      <h2 className="twilio-platform__card-title">Readiness checklist</h2>
                      <p className="twilio-platform__card-sub">
                        Quick view of what restaurants will receive.
                      </p>
                    </div>
                  </div>
                  <div className="twilio-platform__card-body">
                    <ul className="twilio-platform__checklist">
                      {checklist.map((item) => {
                        const isOk = item.ok;
                        const isOptionalMissing = item.optional && !item.ok;
                        return (
                          <li key={item.key}>
                            <span
                              className={`twilio-platform__check ${
                                isOk || isOptionalMissing
                                  ? "twilio-platform__check--ok"
                                  : "twilio-platform__check--miss"
                              }`}
                              style={
                                isOptionalMissing ? { opacity: 0.55 } : undefined
                              }
                            >
                              <i
                                className={`fas ${
                                  isOk
                                    ? "fa-check"
                                    : isOptionalMissing
                                      ? "fa-minus"
                                      : "fa-xmark"
                                }`}
                                aria-hidden
                              />
                            </span>
                            <span>
                              {item.label}
                              {isOptionalMissing ? " (optional)" : ""}
                            </span>
                          </li>
                        );
                      })}
                    </ul>

                    <p className="twilio-platform__help mt-3">
                      <strong>Tip:</strong> Create an API Key in the Twilio Console under
                      Account → API keys &amp; tokens. Auth Token is only needed for
                      inbound webhook validation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </GridSectionLoader>
    </div>
  );
};

export default ManageTwilioPlatformConfig;
